import {
  productUpdateRowKey,
  type ProductUpdateData,
} from '../services/productUpdates';

/** Per-user key; one viewed version string per product update id (never a list). */
const STORAGE_KEY_PREFIX = 'roger-product-update-viewed-versions';

type ViewedVersionByUpdateId = Record<string, string>;

function storageKeyForUser(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

/** Coerce stored values to a single version string per update id (overwrite, never append). */
function normalizeViewedVersionByUpdateId(
  raw: unknown
): ViewedVersionByUpdateId {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const result: ViewedVersionByUpdateId = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') {
      result[key] = value;
    } else if (Array.isArray(value)) {
      const last = value[value.length - 1];
      if (typeof last === 'string') {
        result[key] = last;
      }
    }
  }
  return result;
}

function readViewedVersionsForUser(userId: string): ViewedVersionByUpdateId {
  try {
    const raw = localStorage.getItem(storageKeyForUser(userId));
    if (raw) {
      return normalizeViewedVersionByUpdateId(JSON.parse(raw));
    }
  } catch (error) {
    console.error(`Failed to read viewed versions for user ${userId}`, error);
  }
  return {};
}

function writeViewedVersionsForUser(
  userId: string,
  data: ViewedVersionByUpdateId
): void {
  localStorage.setItem(storageKeyForUser(userId), JSON.stringify(data));
}

/**
 * Build viewed map capped to active product updates from the API.
 * Each update id holds at most one version string (overwrites on re-view).
 */
function mergeViewedAfterDisplay(
  previous: ViewedVersionByUpdateId,
  currentFromApi: ProductUpdateData[],
  newlyViewed: ProductUpdateData[]
): ViewedVersionByUpdateId {
  const newlyViewedByKey = new Map(
    newlyViewed.map(update => [productUpdateRowKey(update), update.version])
  );
  const result: ViewedVersionByUpdateId = {};
  for (const update of currentFromApi) {
    const key = productUpdateRowKey(update);
    const markedVersion = newlyViewedByKey.get(key);
    if (markedVersion !== undefined) {
      result[key] = markedVersion;
    } else if (previous[key] === update.version) {
      result[key] = update.version;
    }
  }
  return result;
}

export function getViewedVersionsForUser(
  userId: string
): ViewedVersionByUpdateId {
  return readViewedVersionsForUser(userId);
}

export function filterUnviewedProductUpdates(
  updates: ProductUpdateData[],
  userId: string
): ProductUpdateData[] {
  const viewed = getViewedVersionsForUser(userId);
  return updates.filter(
    update => viewed[productUpdateRowKey(update)] !== update.version
  );
}

/**
 * Record viewed versions for the displayed updates.
 * Storage is pruned to product update ids present in `currentFromApi` only.
 */
export function markProductUpdatesViewed(
  newlyViewed: ProductUpdateData[],
  userId: string,
  currentFromApi: ProductUpdateData[]
): void {
  if (newlyViewed.length === 0 && currentFromApi.length === 0) {
    return;
  }
  const previous = readViewedVersionsForUser(userId);
  const merged = mergeViewedAfterDisplay(previous, currentFromApi, newlyViewed);
  writeViewedVersionsForUser(userId, merged);
}
